import {isAttendanceChatbotEnabled, isFaqChatbotEnabled} from 'common/HelperFunctions'
import {calculateErrorMessage} from 'common/helpers'
import React, {useEffect, useState} from 'react'
import {useLocation} from 'react-router'

import AssistantChatbotPage from './modes/AssistantChatbotPage'
import {getChatSurface} from './modes/assistant/chatSurface'
import {useChatViewer} from './modes/assistant/useChatViewer'

const HIGHLIGHT_LAST_SHOWN_KEY = 'chatbot-highlight-last-shown'
/** Matches the first-open pulse feel — a few cycles, then quiet. */
const DAILY_HIGHLIGHT_DURATION_MS = 6000

/** Recurring (daily) launcher highlight, replacing the once-ever intro pulse semantics. */
function shouldHighlightToday(): boolean {
  try {
    return localStorage.getItem(HIGHLIGHT_LAST_SHOWN_KEY) !== new Date().toDateString()
  } catch (e) {
    calculateErrorMessage(e)
    return false
  }
}

/**
 * Single mount point for the floating "Versi AI Assistant" widget. Decides which
 * backend boards this user gets (attendance: hostname/tenant check; knowledge base:
 * `knowledge_base` permission) and renders nothing when neither applies. Routing
 * between the boards happens per message inside the page — there is one tab.
 *
 * Visitors, applicants, students and parents on `/admissions/*` hold no portal permissions, so
 * there the assistant renders regardless of those gates and talks to an admission endpoint, with
 * neither board. ADMIN / TEACHER / STAFF get the general surface everywhere, gates included.
 */
const ChatbotHost: React.FC = () => {
  const {pathname} = useLocation()
  const viewer = useChatViewer()
  const isApplicantSurface = getChatSurface(pathname, viewer) === 'admission_applicant'
  const attendanceEnabled = !isApplicantSurface && isAttendanceChatbotEnabled()
  const faqEnabled = !isApplicantSurface && isFaqChatbotEnabled()

  const [dailyHighlight, setDailyHighlight] = useState(() => shouldHighlightToday())

  useEffect(() => {
    if (!dailyHighlight) return
    try {
      localStorage.setItem(HIGHLIGHT_LAST_SHOWN_KEY, new Date().toDateString())
    } catch (e) {
      calculateErrorMessage(e)
    }
    const timer = window.setTimeout(() => setDailyHighlight(false), DAILY_HIGHLIGHT_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [dailyHighlight])

  if (!isApplicantSurface && !attendanceEnabled && !faqEnabled) return null

  return (
    <AssistantChatbotPage
      attendanceEnabled={attendanceEnabled}
      faqEnabled={faqEnabled}
      dailyHighlight={dailyHighlight}
    />
  )
}

export default ChatbotHost
